import { Inject, Injectable, Optional } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Observable, ReplaySubject, Subject } from 'rxjs'
import { JML_LIB_CONFIG } from '../jml-lib-config.token'
import { JmlLibConfig } from '../jml-lib-config'
import { filter, map, mergeMap } from 'rxjs/operators'
import { ServerInfo } from '../models/app-config/server-info.model'
import { UIInfo } from '../models/app-config/ui-info.model'
import { Router } from '@angular/router'

/**
 * Special service, which reads data needed for the initialization of the app. (backend-url, ui-/server-infos)
 *
 * Contains static properties and methods. These are ONLY to be used in places, where service-injection is not feasible. (Like module initialization).
 * For all other places please refrain from using the static properties and use service-injection instead.
 */
@Injectable({
  providedIn: 'root'
})
export class AppConfigService {
  // we keep this as a local copy to deal with baseUrl overrides
  private static uiInfo: UIInfo = undefined

  private readonly JSON_FILE = './assets/config/postbuildconfig.json'

  private postbuildconfigLoader$: Observable<void>
  private serverInfoLoader$: Observable<void>
  private $newBaseUrlAvailable: Subject<boolean> = new Subject<boolean>()

  private $uiInfo = new ReplaySubject<UIInfo>(1)
  private $serverInfo = new ReplaySubject<ServerInfo>(1)

  static getBaseUrl(): string {
    return AppConfigService.uiInfo?.baseUrl
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    // read environment data
    @Optional() @Inject(JML_LIB_CONFIG) private jmlLibConfig: JmlLibConfig
  ) {
    this.postbuildconfigLoader$ = this.http.get(this.JSON_FILE).pipe(
      map((json) => {
        console.log('postbuildconfig loaded')
        const uiInfo = json as UIInfo
        AppConfigService.uiInfo = uiInfo
        const baseUrl = this.buildBaseURL(uiInfo)
        AppConfigService.uiInfo.baseUrl = baseUrl
        console.log('BaseURL: ', AppConfigService.getBaseUrl())
        this.$uiInfo.next(AppConfigService.uiInfo)
        this.$newBaseUrlAvailable.next(true)
      })
    )

    this.serverInfoLoader$ = this.$newBaseUrlAvailable.asObservable().pipe(
      mergeMap(() => this.http.get<ServerInfo>(AppConfigService.getBaseUrl() + '/admin/info')),
      map((serverInfo) => {
        this.$serverInfo.next(serverInfo)
      })
    )
  }

  private buildBaseURL(uiInfo: UIInfo): string {
    let baseUrl = uiInfo.baseUrl

    if (!baseUrl || baseUrl === '#base_url#') {
      console.log('taking Backend URL from environment')
      baseUrl = this.jmlLibConfig.baseUrl
    }
    return baseUrl.replace('${HOST}', this.getHostName())
  }

  getUIInfo$() {
    return this.$uiInfo.asObservable().pipe(filter((uiInfo) => !!uiInfo))
  }

  getServerInfo$() {
    return this.$serverInfo.asObservable().pipe(filter((serverInfo) => !!serverInfo))
  }

  getBaseUrl$(): Observable<string> {
    return this.getUIInfo$().pipe(map((uiInfo) => uiInfo.baseUrl))
  }

  load() {
    this.postbuildconfigLoader$.subscribe()
    this.serverInfoLoader$.subscribe({
      error: (e) => {
        console.error('backend unreachable. redirect to error screen')
        this.router.navigate(['/connection-failed'])
      }
    })
  }

  /** overrides the default base URL for the backend. */
  overwriteBaseUrl(newUrl) {
    AppConfigService.uiInfo.baseUrl = newUrl
    this.$uiInfo.next(AppConfigService.uiInfo)
    this.$newBaseUrlAvailable.next(true)
  }

  private getHostName(): string {
    return `${window.location.protocol}//${window.location.host}`
  }
}
